import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { Label } from "../../../ui/common/label";
import { RadioGroup, RadioGroupItem } from "../../../ui/common/radio-group";
import { EDIT_MAPPING_COPY } from "../constants/mappingVersion.constants";
import { EditMode } from "../types/mappingVersion.types";

interface EditModeRadioGroupProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  /** Blocks picking "Create a new version", e.g. while the default version isn't calculated yet. */
  disableNewVersion?: boolean;
  restrictToUpdateOnly?: boolean;
}

/**
 * No shared FormRadioGroup atom exists in the UI kit yet; this stays local to the feature
 * rather than adding one to the shared kit for a single consumer.
 */
function EditModeRadioGroup<TFieldValues extends FieldValues>({
  control,
  name,
  disableNewVersion = false,
  restrictToUpdateOnly = false
}: EditModeRadioGroupProps<TFieldValues>) {
  return (
    <div className="tw-space-y-2">
      <Label>{EDIT_MAPPING_COPY.modeLabel}</Label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <RadioGroup value={field.value} onValueChange={field.onChange} className="tw-space-y-2">
            <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-p-3">
              <RadioGroupItem value={EditMode.Update} id="mode-update" className="tw-mt-0.5" />
              <Label htmlFor="mode-update" className="tw-cursor-pointer tw-font-normal">
                {EDIT_MAPPING_COPY.updateOptionLabel}
                <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-muted-foreground">
                  {EDIT_MAPPING_COPY.updateOptionDescription}
                </span>
              </Label>
            </div>
            {!restrictToUpdateOnly ? (
              <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-p-3">
                <RadioGroupItem
                  value={EditMode.NewVersion}
                  id="mode-new"
                  className="tw-mt-0.5"
                  disabled={disableNewVersion}
                />
                <Label
                  htmlFor="mode-new"
                  className={`tw-font-normal ${
                    disableNewVersion ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer"
                  }`}
                >
                  {EDIT_MAPPING_COPY.newVersionOptionLabel}
                  <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-muted-foreground">
                    {disableNewVersion
                      ? EDIT_MAPPING_COPY.newVersionOptionDisabledReason
                      : EDIT_MAPPING_COPY.newVersionOptionDescription}
                  </span>
                </Label>
              </div>
            ) : null}
          </RadioGroup>
        )}
      />
    </div>
  );
}

export default EditModeRadioGroup;
